import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { format } from 'date-fns';
import {
  ArrowLeft,
  FileText,
  UploadSimple,
  DownloadSimple,
  Spinner,
  CheckCircle,
  Clock,
  Package,
  Truck,
  Storefront,
  Phone,
  Envelope,
  MapPin,
  CalendarBlank,
  CurrencyDollar,
  Receipt,
  ClipboardText,
  Anchor,
  AirplaneTakeoff,
  Path,
  SealCheck,
  User,
  XCircle,
  Warning,
  PaperPlaneTilt,
  Files
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CURRENCY_SYMBOLS = { USD: '$', XCD: 'EC$', JMD: 'J$', TTD: 'TT$' };

const STATUS_LABELS = {
  draft: 'Draft',
  requisition_approved: 'Requisition Approved',
  po_issued: 'PO Issued',
  vendor_confirmed: 'Vendor Confirmed',
  documents_uploaded: 'Documents Uploaded',
  in_transit: 'In Transit',
  goods_received: 'Goods Received',
  delivered: 'Delivered',
  closed: 'Closed'
};

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600',
  requisition_approved: 'bg-slate-200 text-slate-700',
  po_issued: 'bg-[#0F52BA]/10 text-[#0F52BA]',
  vendor_confirmed: 'bg-[#38B2AC]/10 text-[#0F766E]',
  documents_uploaded: 'bg-[#F4A261]/10 text-[#B45309]',
  in_transit: 'bg-[#2D6A4F]/10 text-[#2D6A4F]',
  goods_received: 'bg-[#059669]/10 text-[#059669]',
  delivered: 'bg-[#38B2AC]/10 text-[#0F766E]',
  closed: 'bg-[#2D6A4F] text-white'
};

const DOCUMENT_TYPE_LABELS = {
  original_vendor_invoice: 'Invoice',
  invoice: 'Invoice',
  vendor_invoice: 'Invoice',
  commercial_invoice: 'Invoice',
  proforma_invoice: 'Invoice',
  packing_list: 'Packing List',
  hazmat_documents: 'Hazmat Documents',
  material_data_safety_sheet: 'Material Data Safety Sheet',
  certificate_of_origin: 'Certificate of Origin',
  bill_of_lading: 'Bill of Lading',
  receiving_confirmation: 'Receiving Confirmation',
  other: 'Other Document'
};

// Timeline Stage Component for 8-stage approval flow
const TimelineStage = ({ stageNumber, title, icon, isComplete, isRejected, isPending, isException, isOverdue, isActive, isNotReached, children }) => {
  const getBorderColor = () => {
    if (isRejected || isOverdue) return 'border-[#E63946]';
    if (isException) return 'border-[#F4A261]';
    if (isActive) return 'border-[#8B5CF6]';
    if (isComplete) return 'border-[#2D6A4F]';
    if (isNotReached) return 'border-slate-200';
    return 'border-slate-200';
  };
  
  const getIconBgColor = () => {
    if (isRejected || isOverdue) return 'bg-[#E63946] text-white';
    if (isException) return 'bg-[#F4A261] text-white';
    if (isActive) return 'bg-[#8B5CF6] text-white';
    if (isComplete) return 'bg-[#2D6A4F] text-white';
    if (isNotReached) return 'bg-slate-100 text-slate-400';
    return 'bg-slate-100 text-slate-400';
  };

  return (
    <div className={`relative border-l-4 ${getBorderColor()} pl-6 pb-4`}>
      {/* Stage number circle */}
      <div className={`absolute -left-[22px] top-0 w-10 h-10 rounded-full flex items-center justify-center ${getIconBgColor()}`}>
        {isComplete && !isActive && !isRejected && !isException && !isOverdue ? (
          <CheckCircle size={20} weight="fill" />
        ) : (
          icon
        )}
      </div>
      
      {/* Stage content */}
      <div className="ml-2">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs font-semibold uppercase tracking-wider ${isActive ? 'text-[#8B5CF6]' : 'text-slate-400'}`}>
            Stage {stageNumber}
            {isActive && <span className="ml-2 px-2 py-0.5 bg-[#8B5CF6]/10 rounded text-[#8B5CF6] normal-case">Current</span>}
          </span>
        </div>
        <h3 className={`text-base font-semibold mb-3 ${isNotReached ? 'text-slate-400' : 'text-slate-800'}`}>{title}</h3>
        {!isNotReached && children}
        {isNotReached && <p className="text-sm text-slate-400 italic">Not yet reached</p>}
      </div>
    </div>
  );
};

const PurchaseOrderDetailPage = () => {
  const { poId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [po, setPO] = useState(null);
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);

  // Upload form
  const [documentType, setDocumentType] = useState('commercial_invoice');
  const [documentUrl, setDocumentUrl] = useState('');
  const [filename, setFilename] = useState('');
  
  // Receiving Module (Stage 9)
  const [receivingDialogOpen, setReceivingDialogOpen] = useState(false);
  const [receivingItems, setReceivingItems] = useState([]);
  const [receivingNotes, setReceivingNotes] = useState('');
  const [receivingSubmitting, setReceivingSubmitting] = useState(false);

  // Assign Freight Forwarder (inline, Stage 7)
  const [assignFFDialogOpen, setAssignFFDialogOpen] = useState(false);
  const [freightForwarders, setFreightForwarders] = useState([]);
  const [selectedFFId, setSelectedFFId] = useState('');
  const [cargoReadyDate, setCargoReadyDate] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [assigningFF, setAssigningFF] = useState(false);
  
  const isPurchasingManager = user?.role === 'purchasing_manager' || user?.role === 'system_admin';

  useEffect(() => {
    fetchPODetails();
  }, [poId]);

  const fetchPODetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/purchase-orders/${poId}`, getAuthHeaders());
      setPO(response.data);
      
      // Fetch shipment data for this PO
      try {
        const shipmentsRes = await axios.get(`${API_URL}/api/shipments`, getAuthHeaders());
        const poShipment = shipmentsRes.data.find(s => s.po_id === poId);
        if (poShipment) {
          setShipment(poShipment);
        }
      } catch (e) {
        // No shipment data, that's okay
      }
    } catch (error) {
      toast.error('Failed to load purchase order details');
      navigate('/purchase-orders');
    } finally {
      setLoading(false);
    }
  };

  const downloadPOPdf = async (id) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API_URL}/api/purchase-orders/${id}/pdf`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'purchase_order.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleUploadDocument = async () => {
    if (!documentUrl || !filename) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/purchase-orders/${poId}/documents`, {
        document_type: documentType,
        document_url: documentUrl,
        filename: filename
      }, getAuthHeaders());
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setDocumentUrl('');
      setFilename('');
      fetchPODetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClosePO = async () => {
    if (!confirm('Are you sure you want to close this purchase order? This action cannot be undone.')) return;
    setClosing(true);
    try {
      await axios.put(`${API_URL}/api/purchase-orders/${poId}/close`, {}, getAuthHeaders());
      toast.success('Purchase order closed successfully');
      fetchPODetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close purchase order');
    } finally {
      setClosing(false);
    }
  };

  // Receiving Module Functions
  const openReceivingDialog = () => {
    // Initialize receiving items from PO line items
    if (po?.line_items) {
      const items = po.line_items.map(item => ({
        item_name: item.item_name,
        ordered_quantity: item.quantity,
        received_quantity: item.quantity, // Default to ordered qty
        unit: item.unit,
        has_variance: false,
        variance_note: ''
      }));
      setReceivingItems(items);
    }
    setReceivingNotes('');
    setReceivingDialogOpen(true);
  };

  const updateReceivedQuantity = (index, qty) => {
    const newItems = [...receivingItems];
    newItems[index].received_quantity = parseInt(qty) || 0;
    newItems[index].has_variance = newItems[index].received_quantity !== newItems[index].ordered_quantity;
    setReceivingItems(newItems);
  };

  const updateVarianceNote = (index, note) => {
    const newItems = [...receivingItems];
    newItems[index].variance_note = note;
    setReceivingItems(newItems);
  };

  const handleConfirmReceiving = async () => {
    // Validate variances have notes
    const hasVarianceWithoutNote = receivingItems.some(
      item => item.has_variance && (!item.variance_note || !item.variance_note.trim())
    );
    
    if (hasVarianceWithoutNote) {
      toast.error('Please provide variance notes for all items with quantity differences');
      return;
    }
    
    setReceivingSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/purchase-orders/${poId}/receive`, {
        line_items: receivingItems,
        overall_notes: receivingNotes
      }, getAuthHeaders());
      
      toast.success('Goods received confirmed! Financial Controller has been notified.');
      setReceivingDialogOpen(false);
      fetchPODetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm receiving');
    } finally {
      setReceivingSubmitting(false);
    }
  };

  // Assign Freight Forwarder (inline, Stage 7)
  const openAssignFFDialog = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/freight-forwarders`, getAuthHeaders());
      setFreightForwarders(res.data || []);
    } catch (error) {
      toast.error('Failed to load freight forwarders');
      return;
    }
    setSelectedFFId(po?.assigned_freight_forwarder_id || '');
    const defaultDate = po?.cargo_ready_date
      ? new Date(po.cargo_ready_date)
      : new Date();
    setCargoReadyDate(defaultDate.toISOString().slice(0, 10));
    setAssignmentNotes('');
    setAssignFFDialogOpen(true);
  };

  const handleAssignFF = async () => {
    if (!selectedFFId) {
      toast.error('Please select a freight forwarder');
      return;
    }
    if (!cargoReadyDate) {
      toast.error('Please select an estimated cargo ready date');
      return;
    }
    setAssigningFF(true);
    try {
      await axios.put(
        `${API_URL}/api/purchase-orders/${poId}/assign-freight-forwarder`,
        {
          freight_forwarder_id: selectedFFId,
          cargo_ready_date: new Date(cargoReadyDate).toISOString(),
          notes: assignmentNotes,
        },
        getAuthHeaders()
      );
      toast.success('Freight forwarder assigned successfully');
      setAssignFFDialogOpen(false);
      fetchPODetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    } finally {
      setAssigningFF(false);
    }
  };

  const formatCurrency = (value) => {
    if (!po) return '';
    const symbol = CURRENCY_SYMBOLS[po.currency] || '$';
    const numValue = value || 0;
    return `${symbol}${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const subtotal = po?.line_items?.reduce((sum, item) => {
    const itemTotal = item.total_price || (item.quantity * (item.unit_price || 0));
    return sum + itemTotal;
  }, 0) || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F52BA] border-t-transparent"></div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="text-center py-16">
        <FileText size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Purchase order not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/purchase-orders')}
        className="mb-2"
        data-testid="back-to-pos-btn"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to Purchase Orders
      </Button>

      {/* PO Header Section */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="po-header-section">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          {/* Left side - PO Info */}
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl gradient-ocean flex items-center justify-center">
                  <FileText size={28} weight="fill" className="text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="po-number">
                    {po.po_number}
                  </h1>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[po.status]}`} data-testid="po-status-badge">
                    {STATUS_LABELS[po.status]}
                  </span>
                </div>
              </div>
              {/* Download PO as PDF Button */}
              <Button
                onClick={() => downloadPOPdf(po.id)}
                className="bg-[#3D1D72] hover:bg-[#2D1652]"
                data-testid="download-po-pdf-btn"
              >
                <DownloadSimple size={18} className="mr-2" />
                Download PO as PDF
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="flex items-start gap-3">
                <CalendarBlank size={20} className="text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Date Issued</p>
                  <p className="text-sm font-medium text-slate-800" data-testid="po-issued-date">
                    {po.issued_at ? format(new Date(po.issued_at), 'MMM d, yyyy') : 'Not issued yet'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Package size={20} className="text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Delivery Date</p>
                  <p className="text-sm font-medium text-slate-800" data-testid="po-delivery-date">
                    {format(new Date(po.required_delivery_date), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CurrencyDollar size={20} className="text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Currency</p>
                  <p className="text-sm font-medium text-slate-800" data-testid="po-currency">
                    {po.currency} ({CURRENCY_SYMBOLS[po.currency]})
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Receipt size={20} className="text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Payment Terms</p>
                  <p className="text-sm font-medium text-slate-800" data-testid="po-payment-terms">
                    {po.payment_terms}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Vendor Info */}
          {po.vendor_details && (
            <div className="lg:w-80 p-4 bg-slate-50 rounded-xl" data-testid="vendor-details-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#38B2AC]/10 flex items-center justify-center">
                  <Storefront size={20} weight="duotone" className="text-[#38B2AC]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Vendor</p>
                  <p className="font-semibold text-slate-800" data-testid="vendor-name">{po.vendor_details.company_name}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <div className="w-5 flex justify-center"><span className="text-slate-400">👤</span></div>
                  <span data-testid="vendor-contact">{po.vendor_details.contact_person}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Envelope size={16} className="text-slate-400" />
                  <span data-testid="vendor-email">{po.vendor_details.email}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone size={16} className="text-slate-400" />
                  <span data-testid="vendor-phone">{po.vendor_details.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin size={16} className="text-slate-400" />
                  <span data-testid="vendor-country">{po.vendor_details.country}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Confirm Goods Received Button - Show for in_transit POs */}
        {isPurchasingManager && po.status === 'in_transit' && (
          <div className="mt-6 pt-6 border-t border-slate-200 flex justify-end">
            <Button
              onClick={openReceivingDialog}
              className="bg-[#059669] hover:bg-[#047857]"
              data-testid="confirm-goods-received-btn"
            >
              <Package size={18} className="mr-2" />
              Confirm Goods Received
            </Button>
          </div>
        )}
        
        {/* Goods Received Confirmation Banner */}
        {po.status === 'goods_received' && po.receiving_confirmation && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="p-4 bg-[#059669]/10 rounded-lg border border-[#059669]/20">
              <div className="flex items-start gap-3">
                <CheckCircle size={24} weight="fill" className="text-[#059669] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-[#059669]">Goods Received Confirmed</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Received by: {po.receiving_confirmation.received_by_name} on{' '}
                    {format(new Date(po.receiving_confirmation.received_at), 'MMMM d, yyyy \'at\' h:mm a')}
                  </p>
                  {po.receiving_confirmation.has_variances && (
                    <div className="mt-2 px-3 py-2 bg-amber-100 border border-amber-200 rounded-lg">
                      <p className="text-sm font-medium text-amber-800">
                        <Warning size={16} className="inline mr-1" />
                        Variances were noted during receiving
                      </p>
                    </div>
                  )}
                  {/* Receiving Line Items Summary */}
                  <div className="mt-3 border-t border-slate-200 pt-3">
                    <p className="text-xs font-medium text-slate-500 uppercase mb-2">Received Quantities</p>
                    <div className="space-y-1">
                      {po.receiving_confirmation.line_items?.map((item, idx) => (
                        <div key={idx} className={`flex items-center justify-between text-sm p-2 rounded ${item.has_variance ? 'bg-red-50' : 'bg-slate-50'}`}>
                          <span className="font-medium text-slate-700">{item.item_name}</span>
                          <span className={item.has_variance ? 'text-red-600 font-medium' : 'text-slate-600'}>
                            {item.received_quantity} / {item.ordered_quantity} {item.unit}
                            {item.has_variance && ` (${item.variance_note})`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {po.receiving_confirmation.overall_notes && (
                    <p className="mt-3 text-sm text-slate-600 italic">
                      Notes: {po.receiving_confirmation.overall_notes}
                    </p>
                  )}
                  {/* Landed Cost Summary for Overseas POs */}
                  {po.po_type === 'overseas' && po.true_landed_cost && (
                    <div className="mt-4 border-t border-slate-200 pt-3" data-testid="landed-cost-summary">
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Landed Cost Breakdown</p>
                      <div className="bg-purple-50 rounded-lg p-3 space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                          {po.actual_freight > 0 && <div className="flex justify-between"><span className="text-slate-600">Freight</span><span className="font-medium">${po.actual_freight.toLocaleString()}</span></div>}
                          {po.broker_fees > 0 && <div className="flex justify-between"><span className="text-slate-600">Broker Fees</span><span className="font-medium">${po.broker_fees.toLocaleString()}</span></div>}
                          {po.customs_duty > 0 && <div className="flex justify-between"><span className="text-slate-600">Customs Duty</span><span className="font-medium">${po.customs_duty.toLocaleString()}</span></div>}
                          {po.insurance > 0 && <div className="flex justify-between"><span className="text-slate-600">Insurance</span><span className="font-medium">${po.insurance.toLocaleString()}</span></div>}
                          {po.actual_other_charges?.map((ch, i) => (
                            <div key={i} className="flex justify-between"><span className="text-slate-600">{ch.label}</span><span className="font-medium">${ch.amount?.toLocaleString()}</span></div>
                          ))}
                        </div>
                        <div className="border-t border-purple-200 pt-2 flex justify-between items-center">
                          <div>
                            <p className="text-xs text-slate-500">True Landed Cost</p>
                            <p className="text-lg font-bold text-purple-700">${po.true_landed_cost.toLocaleString()}</p>
                          </div>
                          {po.estimated_landed_cost && (
                            <div className="text-right">
                              <p className="text-xs text-slate-500">vs Estimated ${po.estimated_landed_cost.toLocaleString()}</p>
                              <p className={`text-sm font-bold ${po.landed_cost_variance > 0 ? 'text-red-600' : po.landed_cost_variance < 0 ? 'text-green-600' : 'text-slate-600'}`}>
                                Variance: {po.landed_cost_variance > 0 ? '+' : ''}${po.landed_cost_variance?.toLocaleString()}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Per-Item Landed Cost Breakdown — shown whenever charges have been allocated to items, regardless of PO type */}
                  {po.receiving_confirmation?.line_items?.some(li =>
                    (li.allocated_freight || 0) + (li.allocated_trucking || 0) + (li.allocated_broker || 0)
                    + (li.allocated_duty || 0) + (li.allocated_insurance || 0) + (li.allocated_other || 0) > 0
                  ) && (
                    <div className="mt-4 border-t border-slate-200 pt-3" data-testid="per-item-landed-breakdown">
                      <p className="text-xs font-medium text-slate-500 uppercase mb-2">Per-Item Landed Cost Breakdown</p>
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full text-xs" data-testid="per-item-landed-table">
                          <thead className="bg-[#3D1D72] text-white">
                            <tr>
                              <th className="px-2 py-1 text-left">Item / SKU</th>
                              <th className="px-2 py-1 text-right">Qty</th>
                              <th className="px-2 py-1 text-right">Purchase</th>
                              <th className="px-2 py-1 text-right">Freight</th>
                              <th className="px-2 py-1 text-right">Trucking</th>
                              <th className="px-2 py-1 text-right">Broker</th>
                              <th className="px-2 py-1 text-right">Duty</th>
                              <th className="px-2 py-1 text-right">Insurance</th>
                              <th className="px-2 py-1 text-right">Other</th>
                              <th className="px-2 py-1 text-right">Total Landed</th>
                              <th className="px-2 py-1 text-right">True Unit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {po.receiving_confirmation.line_items.map((li, idx) => (
                              <tr key={idx} className={idx % 2 ? 'bg-[#F0EBF8]/40' : 'bg-white'}>
                                <td className="px-2 py-1">
                                  <div className="font-medium text-slate-800">{li.item_name}</div>
                                  {li.sku && <div className="text-[10px] text-slate-500">SKU: {li.sku}</div>}
                                </td>
                                <td className="px-2 py-1 text-right">{li.received_quantity}</td>
                                <td className="px-2 py-1 text-right">${(li.line_value || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(li.allocated_freight || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(li.allocated_trucking || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(li.allocated_broker || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(li.allocated_duty || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(li.allocated_insurance || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(li.allocated_other || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right font-semibold">${(li.true_landed_cost || 0).toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">${(li.cost_per_unit_landed || 0).toFixed(4)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="mt-1 text-[11px] text-slate-500 italic">
                        Freight allocated by {po.allocation_method === 'by_volume' ? 'volume / weight' : 'value'}. All other charges allocated by value.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Close PO Button - Show for purchasing managers when PO is goods_received but not closed */}
        {isPurchasingManager && po.status === 'goods_received' && (
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleClosePO}
              className="bg-[#2D6A4F] hover:bg-[#1E4D38]"
              disabled={closing}
              data-testid="close-po-btn"
            >
              {closing ? (
                <Spinner size={18} className="animate-spin mr-2" />
              ) : (
                <SealCheck size={18} className="mr-2" />
              )}
              Close Purchase Order
            </Button>
          </div>
        )}
        
        {/* Close PO Button - Show for purchasing managers when PO is not already closed and not in_transit or goods_received */}
        {isPurchasingManager && po.status !== 'closed' && po.status !== 'in_transit' && po.status !== 'goods_received' && (
          <div className="mt-6 pt-6 border-t border-slate-200 flex justify-end">
            <Button
              onClick={handleClosePO}
              className="bg-[#2D6A4F] hover:bg-[#1E4D38]"
              disabled={closing}
              data-testid="close-po-btn"
            >
              {closing ? (
                <Spinner size={18} className="animate-spin mr-2" />
              ) : (
                <SealCheck size={18} className="mr-2" />
              )}
              Close Purchase Order
            </Button>
          </div>
        )}
        
        {/* Closed Status Banner */}
        {po.status === 'closed' && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="flex items-center gap-3 p-4 bg-[#2D6A4F]/10 rounded-lg">
              <SealCheck size={24} weight="fill" className="text-[#2D6A4F]" />
              <div>
                <p className="font-medium text-[#2D6A4F]">This Purchase Order is Closed</p>
                {po.closed_at && (
                  <p className="text-sm text-[#2D6A4F]/70">
                    Closed on {format(new Date(po.closed_at), 'MMMM d, yyyy')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Approval Trail Section - Permanent, always visible */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="approval-trail-section">
        <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-[#3D1D72]/5 to-transparent">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#3D1D72]/10 flex items-center justify-center">
                <SealCheck size={20} weight="duotone" className="text-[#3D1D72]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Approval Trail
                </h2>
                <p className="text-xs text-slate-500">Permanent audit record of all approval actions</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid="approval-trail-po-context">
              <span className="px-3 py-1.5 rounded-lg bg-[#2D6A4F]/10 text-[#2D6A4F] text-sm font-semibold">
                PO: {po.po_number}
              </span>
              {(po.requisition_number || po.approval_timeline?.stage2_pr_converted?.pr_number) && (
                <span className="px-3 py-1.5 rounded-lg bg-[#0F52BA]/10 text-[#0F52BA] text-sm font-medium">
                  PR: {po.requisition_number || `PR-${po.approval_timeline.stage2_pr_converted.pr_number}`}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-4">
          <div className="space-y-3">
            {/* Auto-Approved PO (under threshold) */}
            {po.total_value < 500 && (
              <div className="flex items-start gap-4 p-4 bg-[#2D6A4F]/5 rounded-lg border border-[#2D6A4F]/20" data-testid="approval-auto-approved">
                <div className="w-8 h-8 rounded-full bg-[#2D6A4F] flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={18} weight="fill" className="text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-[#2D6A4F] text-white text-xs font-medium rounded">Auto Approved</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800">
                    Approved by: {po.approval_trail?.created_by_name || 'Purchasing Manager'}
                  </p>
                  <p className="text-sm text-slate-600">
                    Role: Purchasing Manager
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {po.approval_trail?.issued_at ? format(new Date(po.approval_trail.issued_at), "MMMM d, yyyy 'at' h:mm a") : 'N/A'}
                  </p>
                </div>
              </div>
            )}
            
            {/* FC Approval Required (above threshold) */}
            {po.total_value >= 500 && (
              <>
                {/* Row 1: Submitted by PM */}
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="approval-submitted">
                  <div className="w-8 h-8 rounded-full bg-[#3D1D72] flex items-center justify-center flex-shrink-0">
                    <ClipboardText size={18} weight="fill" className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-[#3D1D72] text-white text-xs font-medium rounded">Submitted for Approval</span>
                    </div>
                    <p className="text-sm font-medium text-slate-800">
                      Submitted by: {po.approval_trail?.created_by_name || 'Purchasing Manager'}
                    </p>
                    <p className="text-sm text-slate-600">
                      Role: Purchasing Manager
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {po.approval_trail?.created_at ? format(new Date(po.approval_trail.created_at), "MMMM d, yyyy 'at' h:mm a") : 'N/A'}
                    </p>
                  </div>
                </div>
                
                {/* Row 2 (optional): Queried by FC */}
                {po.approval_trail?.fc_queried && (
                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="approval-queried">
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <Warning size={18} weight="fill" className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-medium rounded">Query Raised</span>
                      </div>
                      <p className="text-sm font-medium text-slate-800">
                        Queried by: {po.approval_trail?.fc_queried_by_name || 'Financial Controller'}
                      </p>
                      <p className="text-sm text-slate-600">
                        Role: Financial Controller
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {po.approval_trail?.fc_queried_at ? format(new Date(po.approval_trail.fc_queried_at), "MMMM d, yyyy 'at' h:mm a") : 'N/A'}
                      </p>
                      {po.approval_trail?.fc_query_reason && (
                        <p className="text-sm text-amber-700 mt-2 p-2 bg-amber-100 rounded">
                          Query: {po.approval_trail.fc_query_reason}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Row 3: FC Approved */}
                {po.approval_trail?.fc_approved && (
                  <div className="flex items-start gap-4 p-4 bg-[#2D6A4F]/5 rounded-lg border border-[#2D6A4F]/20" data-testid="approval-fc-approved">
                    <div className="w-8 h-8 rounded-full bg-[#2D6A4F] flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={18} weight="fill" className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-[#2D6A4F] text-white text-xs font-medium rounded">FC Approved</span>
                      </div>
                      <p className="text-sm font-medium text-slate-800">
                        Approved by: {po.approval_trail?.fc_approved_by_name || 'Financial Controller'}
                      </p>
                      <p className="text-sm text-slate-600">
                        Role: Financial Controller
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {po.approval_trail?.fc_approved_at ? format(new Date(po.approval_trail.fc_approved_at), "MMMM d, yyyy 'at' h:mm a") : 'N/A'}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Pending FC Approval */}
                {!po.approval_trail?.fc_approved && po.status === 'pending_approval' && (
                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="approval-pending">
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <Clock size={18} weight="fill" className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-medium rounded">Pending FC Approval</span>
                      </div>
                      <p className="text-sm text-slate-600">
                        Awaiting Financial Controller review and approval
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="line-items-section">
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Line Items
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Item Description</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Quantity</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit Price</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Line Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {po.line_items.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50" data-testid={`line-item-${idx}`}>
                  <td className="px-4 py-4 text-sm text-slate-800 font-medium">{item.item_name}</td>
                  <td className="px-4 py-4 text-sm text-slate-600 text-center">{item.quantity}</td>
                  <td className="px-4 py-4 text-sm text-slate-600 text-center">{item.unit || item.unit_of_measure || 'units'}</td>
                  <td className="px-4 py-4 text-sm text-slate-600 text-right">{formatCurrency(item.unit_price || item.estimated_unit_price || 0)}</td>
                  <td className="px-4 py-4 text-sm text-slate-800 font-medium text-right">{formatCurrency(item.total_price || (item.quantity * (item.unit_price || item.estimated_unit_price || 0)))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr>
                <td colSpan="4" className="px-4 py-3 text-right text-sm font-medium text-slate-600">Subtotal:</td>
                <td className="px-4 py-3 text-right text-sm font-medium text-slate-800" data-testid="subtotal">{formatCurrency(subtotal)}</td>
              </tr>
              <tr className="border-t border-slate-200">
                <td colSpan="4" className="px-4 py-4 text-right text-base font-bold text-slate-700">Total:</td>
                <td className="px-4 py-4 text-right text-xl font-bold text-[#0F52BA]" data-testid="total-value">{formatCurrency(po.total_value)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Documents Checklist — dynamic list of uploaded docs + FF In-Transit confirmation (OVERSEAS POs) */}
      {po.po_type !== 'local' && (() => {
        const vendorDocs = (po.vendor_documents && po.vendor_documents.length > 0)
          ? po.vendor_documents
          : (po.documents || []).filter(d => d.type !== 'receiving_confirmation');
        const ffDocs = po.ff_documents || [];
        const bolNumber = shipment?.bill_of_lading || po.approval_timeline?.stage7_freight_assigned?.bill_of_lading || null;
        const derivedBol = (!ffDocs.find(d => ['bill_of_lading', 'bol'].includes(d.type)) && bolNumber)
          ? [{
              type: 'bill_of_lading',
              filename: `BoL #${bolNumber}`,
              uploaded_by_name: 'Freight Forwarder',
              uploaded_at: shipment?.updated_at || shipment?.created_at,
            }]
          : [];
        const vendorEntries = vendorDocs.map(d => ({ ...d, source: 'Vendor' }));
        const ffEntries = [...ffDocs, ...derivedBol].map(d => ({ ...d, source: 'Freight Forwarder' }));
        const allEntries = [...vendorEntries, ...ffEntries].sort((a, b) => {
          const ta = a.uploaded_at ? new Date(a.uploaded_at).getTime() : 0;
          const tb = b.uploaded_at ? new Date(b.uploaded_at).getTime() : 0;
          return tb - ta;
        });
        const inTransitOrLater = ['in_transit', 'goods_received', 'delivered', 'closed'].includes(po.status);
        const confirmation = po.in_transit_confirmation;
        return (
          <div
            className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden"
            data-testid="document-checklist-section"
          >
            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
              <div>
                <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Documents Checklist
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Documents actually uploaded for this shipment. The Freight Forwarder confirms which documents are relevant before moving to <span className="font-medium">In Transit</span>.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700"
                  data-testid="checklist-progress"
                >
                  {allEntries.length} {allEntries.length === 1 ? 'document' : 'documents'} uploaded
                </span>
                {inTransitOrLater && (
                  <span className="px-2 py-1 rounded bg-emerald-600 text-white text-xs font-semibold" data-testid="in-transit-badge">
                    In Transit ✓
                  </span>
                )}
              </div>
            </div>
            {allEntries.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="checklist-empty">
                No documents uploaded yet for this shipment.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {allEntries.map((doc, idx) => {
                  const label = DOCUMENT_TYPE_LABELS[doc.type] || doc.type || 'Document';
                  const uploader = doc.uploaded_by_name || doc.uploaded_by || doc.source;
                  return (
                    <div
                      key={`${doc.source}-${idx}`}
                      className="p-4 flex items-center justify-between"
                      data-testid={`checklist-doc-${idx}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center bg-emerald-100 text-emerald-700">
                          <CheckCircle size={20} weight="fill" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">
                            {label}
                            <span className="ml-2 text-xs font-normal text-slate-500">(from {doc.source})</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {doc.filename}
                            {doc.uploaded_at ? ` · ${format(new Date(doc.uploaded_at), 'MMM d, yyyy')}` : ''}
                            {uploader ? ` · uploaded by ${uploader}` : ''}
                          </p>
                        </div>
                      </div>
                      <span className="px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-700">
                        Received
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {confirmation && (
              <div
                className="px-4 py-3 bg-emerald-50/60 border-t border-emerald-100 text-xs text-emerald-900"
                data-testid="in-transit-confirmation-note"
              >
                <p className="font-semibold text-emerald-800 mb-0.5">
                  <CheckCircle size={14} weight="fill" className="inline mr-1" />
                  Freight Forwarder In-Transit Confirmation
                </p>
                <p className="whitespace-pre-line">{confirmation.note}</p>
                <p className="text-[11px] text-emerald-700 mt-1">
                  Confirmed by {confirmation.confirmed_by_name}
                  {confirmation.freight_forwarder_name ? ` (${confirmation.freight_forwarder_name})` : ''}
                  {confirmation.confirmed_at ? ` on ${format(new Date(confirmation.confirmed_at), 'MMM d, yyyy · p')}` : ''}
                </p>
              </div>
            )}
            {!inTransitOrLater && (
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-600">
                The Freight Forwarder must confirm all relevant documents have been uploaded before this shipment can move to <strong>In Transit</strong>.
              </div>
            )}
          </div>
        );
      })()}

      {/* Document Vault - OVERSEAS POs ONLY */}
      {po.po_type !== 'local' && (
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="document-vault-section">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Document Vault
            </h2>
            <p className="text-xs text-slate-500 mt-1">Vendor Documents — Invoices (any invoice showing item descriptions & values) and supporting documents</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="vault-po-context">
              <span className="px-3 py-1 rounded-lg bg-[#2D6A4F]/10 text-[#2D6A4F] text-sm font-semibold">
                PO: {po.po_number}
              </span>
              {(po.requisition_number || po.approval_timeline?.stage2_pr_converted?.pr_number) && (
                <span className="px-3 py-1 rounded-lg bg-[#0F52BA]/10 text-[#0F52BA] text-sm font-medium">
                  PR: {po.requisition_number || `PR-${po.approval_timeline.stage2_pr_converted.pr_number}`}
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={() => setUploadDialogOpen(true)}
            className="bg-[#0F52BA] hover:bg-[#0A3D8F]"
            data-testid="upload-document-btn"
          >
            <UploadSimple size={18} className="mr-2" />
            Upload Document
          </Button>
        </div>
        
        {/* System-Generated Documents Section */}
        {(po.status === 'goods_received' || po.status === 'closed' || po.receiving_confirmation) && (
          <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <FileText size={20} weight="duotone" className="text-emerald-700" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-800">Receiving Report</p>
                    <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-semibold rounded">
                      SYSTEM
                    </span>
                    {po.receiving_confirmation?.has_variances && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded">
                        VARIANCES RECORDED
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Goods received on {po.receiving_confirmation?.received_at ? new Date(po.receiving_confirmation.received_at).toLocaleDateString() : 'N/A'} 
                    {' '}by {po.receiving_confirmation?.received_by_name || 'N/A'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`${API_URL}/api/purchase-orders/${po.id}/receiving-report-pdf`, '_blank')}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="download-receiving-report-btn"
              >
                <DownloadSimple size={16} className="mr-2" />
                Download PDF
              </Button>
            </div>
          </div>
        )}
        
        {po.documents && po.documents.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {/* Sort documents: Original Vendor Invoice first, then Commercial Invoice, then others */}
            {[...po.documents]
              .sort((a, b) => {
                const priority = {
                  'original_vendor_invoice': 1,
                  'commercial_invoice': 2,
                  'packing_list': 3,
                  'hazmat_documents': 4,
                  'material_data_safety_sheet': 5,
                  'certificate_of_origin': 6,
                  'bill_of_lading': 7,
                  'other': 8
                };
                return (priority[a.type] || 99) - (priority[b.type] || 99);
              })
              .map((doc, idx) => {
                const isOriginalInvoice = doc.type === 'original_vendor_invoice';
                const isCommercialInvoice = doc.type === 'commercial_invoice';
                const hasOriginalInvoice = po.documents.some(d => d.type === 'original_vendor_invoice');
                const isPinnedDoc = isOriginalInvoice || (isCommercialInvoice && !hasOriginalInvoice);
                
                return (
                  <div 
                    key={idx} 
                    className={`p-4 flex items-center justify-between ${
                      isOriginalInvoice 
                        ? 'bg-gradient-to-r from-amber-50 to-amber-100/50 border-l-4 border-amber-500' 
                        : isCommercialInvoice && !hasOriginalInvoice
                          ? 'bg-amber-50/30 border-l-4 border-amber-300'
                          : 'hover:bg-slate-50/50'
                    }`} 
                    data-testid={`document-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isOriginalInvoice 
                          ? 'bg-amber-200' 
                          : isCommercialInvoice && !hasOriginalInvoice
                            ? 'bg-amber-100'
                            : 'bg-[#F4A261]/10'
                      }`}>
                        <FileText size={20} weight="duotone" className={
                          isOriginalInvoice 
                            ? 'text-amber-700' 
                            : isCommercialInvoice && !hasOriginalInvoice
                              ? 'text-amber-600'
                              : 'text-[#F4A261]'
                        } />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800" data-testid={`doc-filename-${idx}`}>{doc.filename}</p>
                          {isOriginalInvoice && (
                            <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded">
                              PRIMARY
                            </span>
                          )}
                          {isCommercialInvoice && !hasOriginalInvoice && (
                            <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded">
                              Original Invoice Not Provided
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            isPinnedDoc ? 'bg-amber-100 text-amber-700' : 'bg-slate-100'
                          }`} data-testid={`doc-type-${idx}`}>
                            {DOCUMENT_TYPE_LABELS[doc.type] || doc.type}
                          </span>
                          <span data-testid={`doc-date-${idx}`}>
                            Uploaded {doc.uploaded_at ? format(new Date(doc.uploaded_at), 'MMM d, yyyy') : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {doc.url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(doc.url, '_blank')}
                        data-testid={`download-doc-${idx}`}
                      >
                        <DownloadSimple size={18} className="mr-2" />
                        Download
                      </Button>
                    )}
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="p-8 text-center">
            <ClipboardText size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No vendor documents attached yet</p>
            <p className="text-sm text-amber-600 mt-1">Invoice(s) and supporting documents</p>
          </div>
        )}
      </div>
      )}

      {/* Shipment Details Section - Show if shipment exists or PO is in transit - OVERSEAS ONLY */}
      {po.po_type !== 'local' && (shipment || po.status === 'in_transit') && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="shipment-details-section">
          <div className="p-4 border-b border-slate-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#2D6A4F]/10 flex items-center justify-center">
              <Truck size={18} weight="duotone" className="text-[#2D6A4F]" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Shipment Details
            </h2>
            <span className="ml-auto px-3 py-1 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] text-xs font-medium">
              {shipment?.status === 'in_transit' ? 'In Transit' : shipment?.status || 'In Transit'}
            </span>
          </div>
          
          {shipment ? (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Vessel/Flight Info */}
                <div className="flex items-start gap-3">
                  {shipment.vessel_or_flight?.toLowerCase().includes('mv') || shipment.vessel_or_flight?.toLowerCase().includes('vessel') ? (
                    <Anchor size={20} className="text-[#0F52BA] mt-0.5" />
                  ) : (
                    <AirplaneTakeoff size={20} className="text-[#0F52BA] mt-0.5" />
                  )}
                  <div>
                    <p className="text-xs text-slate-500">Vessel / Flight</p>
                    <p className="text-sm font-medium text-slate-800" data-testid="shipment-vessel">
                      {shipment.vessel_or_flight}
                    </p>
                    {shipment.voyage_flight_number && (
                      <p className="text-xs text-slate-500 mt-0.5">{shipment.voyage_flight_number}</p>
                    )}
                  </div>
                </div>

                {/* Carrier */}
                <div className="flex items-start gap-3">
                  <Truck size={20} className="text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Carrier</p>
                    <p className="text-sm font-medium text-slate-800" data-testid="shipment-carrier">
                      {shipment.carrier_name}
                    </p>
                  </div>
                </div>

                {/* Bill of Lading */}
                {shipment.bill_of_lading && (
                  <div className="flex items-start gap-3">
                    <FileText size={20} className="text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Bill of Lading / Airway Bill</p>
                      <p className="text-sm font-medium text-slate-800" data-testid="shipment-bol">
                        {shipment.bill_of_lading}
                      </p>
                    </div>
                  </div>
                )}

                {/* Pickup Date */}
                <div className="flex items-start gap-3">
                  <CalendarBlank size={20} className="text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Pickup Date</p>
                    <p className="text-sm font-medium text-slate-800" data-testid="shipment-pickup">
                      {format(new Date(shipment.pickup_date), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>

                {/* ETA */}
                <div className="flex items-start gap-3">
                  <Package size={20} className="text-[#2D6A4F] mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Estimated Arrival</p>
                    <p className="text-sm font-bold text-[#2D6A4F]" data-testid="shipment-eta">
                      {format(new Date(shipment.estimated_arrival), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>

                {/* Route */}
                {(shipment.port_of_loading || shipment.port_of_discharge) && (
                  <div className="flex items-start gap-3">
                    <Path size={20} className="text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500">Route</p>
                      <p className="text-sm font-medium text-slate-800" data-testid="shipment-route">
                        {shipment.port_of_loading || 'N/A'} → {shipment.port_of_discharge || 'N/A'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* ETA History */}
              {shipment.eta_history && shipment.eta_history.length > 1 && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-700 mb-3">ETA Update History</p>
                  <div className="space-y-2">
                    {shipment.eta_history.map((eta, idx) => (
                      <div key={idx} className="flex items-center gap-4 text-sm" data-testid={`eta-history-${idx}`}>
                        <span className="text-slate-600 font-medium">
                          {format(new Date(eta.eta), 'MMM d, yyyy')}
                        </span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-500">{eta.reason}</span>
                        <span className="text-slate-400">•</span>
                        <span className="text-xs text-slate-400">
                          {format(new Date(eta.set_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {shipment.notes && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Shipment Notes</p>
                  <p className="text-sm text-slate-700" data-testid="shipment-notes">{shipment.notes}</p>
                </div>
              )}

              {/* Shipment Update Audit Trail — every change to ETA, Sail Date, Vessel, Voyage */}
              {Array.isArray(shipment.shipment_change_history) && shipment.shipment_change_history.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100" data-testid="shipment-update-history">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-slate-700">Shipment Update History</p>
                    <span className="text-xs text-slate-400">
                      {shipment.shipment_change_history.length} {shipment.shipment_change_history.length === 1 ? 'change' : 'changes'}
                    </span>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-slate-100">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
                        <tr>
                          <th className="px-3 py-2 text-left">When</th>
                          <th className="px-3 py-2 text-left">Field</th>
                          <th className="px-3 py-2 text-left">Old Value</th>
                          <th className="px-3 py-2 text-left">New Value</th>
                          <th className="px-3 py-2 text-left">Reason</th>
                          <th className="px-3 py-2 text-left">By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {[...shipment.shipment_change_history].reverse().map((entry, idx) => {
                          const prettyVal = (v) => {
                            if (!v) return '—';
                            const isoDate = /^\d{4}-\d{2}-\d{2}T/.test(v);
                            try {
                              return isoDate ? format(new Date(v), 'MMM d, yyyy') : v;
                            } catch {
                              return v;
                            }
                          };
                          return (
                            <tr key={idx} className="hover:bg-slate-50" data-testid={`shipment-change-row-${idx}`}>
                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                                {format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a')}
                              </td>
                              <td className="px-3 py-2 font-medium text-slate-800">{entry.field_label || entry.field}</td>
                              <td className="px-3 py-2 text-slate-500 line-through">{prettyVal(entry.old_value)}</td>
                              <td className="px-3 py-2 font-medium text-[#0F52BA]">{prettyVal(entry.new_value)}</td>
                              <td className="px-3 py-2 text-slate-600 max-w-xs">{entry.reason || <span className="text-slate-400 italic">First entry</span>}</td>
                              <td className="px-3 py-2 text-slate-600">
                                {entry.changed_by_name}
                                {entry.freight_forwarder_name && (
                                  <span className="text-xs text-slate-400 block">{entry.freight_forwarder_name}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Truck size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Shipment details not yet available</p>
              <p className="text-sm text-slate-400 mt-1">The freight forwarder will log shipment information once cargo is picked up</p>
            </div>
          )}
        </div>
      )}

      {/* 8-Stage PO Journey Timeline */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="approval-timeline-section">
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            PO Journey Timeline
          </h2>
        </div>
        <div className="p-6">
          {po.approval_timeline ? (
            <div className="space-y-6">
              {/* Stage 1: Purchase Requisition Submitted */}
              <TimelineStage
                stageNumber={1}
                title="Purchase Requisition Submitted"
                icon={<ClipboardText size={20} weight="fill" />}
                isComplete={!!po.approval_timeline.stage1_pr_submitted?.submitted_at}
                isActive={po.approval_timeline.current_stage === 1}
                isNotReached={false}
                data-testid="timeline-stage-1"
              >
                {po.approval_timeline.stage1_pr_submitted ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Department:</span>
                      <span className="text-sm font-medium text-slate-800">{po.approval_timeline.stage1_pr_submitted.department_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-slate-400" />
                      <span className="text-sm text-slate-700">{po.approval_timeline.stage1_pr_submitted.requester_name}</span>
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                        {po.approval_timeline.stage1_pr_submitted.requester_role}
                      </span>
                    </div>
                    {po.approval_timeline.stage1_pr_submitted.pr_number && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">PR Number:</span>
                        <span className="text-sm font-medium text-[#0F52BA]">PR-{po.approval_timeline.stage1_pr_submitted.pr_number}</span>
                      </div>
                    )}
                    {po.approval_timeline.stage1_pr_submitted.submitted_at && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <CalendarBlank size={16} className="text-slate-400" />
                        {format(new Date(po.approval_timeline.stage1_pr_submitted.submitted_at), 'MMM d, yyyy')} at {format(new Date(po.approval_timeline.stage1_pr_submitted.submitted_at), 'h:mm a')}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Purchase requisition data not available</p>
                )}
              </TimelineStage>

              {/* Stage 2: Purchase Requisition Converted to PO */}
              <TimelineStage
                stageNumber={2}
                title="Purchase Requisition Converted to PO"
                icon={<CheckCircle size={20} weight="fill" />}
                isComplete={!!po.approval_timeline.stage2_pr_converted?.converted_at}
                isActive={po.approval_timeline.current_stage === 2}
                isNotReached={po.approval_timeline.current_stage < 2}
                data-testid="timeline-stage-2"
              >
                {po.approval_timeline.stage2_pr_converted ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-slate-400" />
                      <span className="text-sm text-slate-700">{po.approval_timeline.stage2_pr_converted.converter_name}</span>
                      <span className="px-2 py-0.5 bg-[#2D6A4F]/10 rounded text-xs text-[#2D6A4F]">
                        {po.approval_timeline.stage2_pr_converted.converter_role}
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-testid="stage2-po-number">
                      <span className="text-sm text-slate-500">PO Number:</span>
                      <span className="text-sm font-medium text-[#2D6A4F]">{po.po_number}</span>
                    </div>
                    {po.approval_timeline.stage2_pr_converted.pr_number && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">Converted from:</span>
                        <span className="text-sm font-medium text-[#0F52BA]">PR-{po.approval_timeline.stage2_pr_converted.pr_number}</span>
                      </div>
                    )}
                    {po.approval_timeline.stage2_pr_converted.converted_at && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <CalendarBlank size={16} className="text-slate-400" />
                        PO Created: {format(new Date(po.approval_timeline.stage2_pr_converted.converted_at), 'MMM d, yyyy')} at {format(new Date(po.approval_timeline.stage2_pr_converted.converted_at), 'h:mm a')}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Awaiting conversion</p>
                )}
              </TimelineStage>

              {/* Stage 3: PO Approval (by Financial Controller/GM) */}
              <TimelineStage
                stageNumber={3}
                title="PO Approval"
                icon={
                  po.approval_timeline.stage3_po_approval?.status === 'approved' ? <CheckCircle size={20} weight="fill" /> :
                  po.approval_timeline.stage3_po_approval?.status === 'rejected' ? <XCircle size={20} weight="fill" /> :
                  <Clock size={20} weight="fill" />
                }
                isComplete={po.approval_timeline.stage3_po_approval?.status === 'approved'}
                isRejected={po.approval_timeline.stage3_po_approval?.status === 'rejected'}
                isActive={po.approval_timeline.current_stage === 3}
                isNotReached={po.approval_timeline.current_stage < 3}
                data-testid="timeline-stage-3"
              >
                {po.approval_timeline.stage3_po_approval ? (
                  <div className="space-y-2">
                    {po.approval_timeline.stage3_po_approval.status === 'approved' && (
                      <>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2D6A4F]/10 rounded-full">
                          <CheckCircle size={16} weight="fill" className="text-[#2D6A4F]" />
                          <span className="text-sm font-medium text-[#2D6A4F]">Approved</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-slate-400" />
                          <span className="text-sm text-slate-700">{po.approval_timeline.stage3_po_approval.approver_name}</span>
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                            {po.approval_timeline.stage3_po_approval.approver_role}
                          </span>
                        </div>
                        {po.approval_timeline.stage3_po_approval.approved_at && (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <CalendarBlank size={16} className="text-slate-400" />
                            {format(new Date(po.approval_timeline.stage3_po_approval.approved_at), 'MMM d, yyyy')} at {format(new Date(po.approval_timeline.stage3_po_approval.approved_at), 'h:mm a')}
                          </div>
                        )}
                        {po.approval_timeline.stage3_po_approval.approval_comments && (
                          <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
                            {po.approval_timeline.stage3_po_approval.approval_comments}
                          </p>
                        )}
                      </>
                    )}
                    {po.approval_timeline.stage3_po_approval.status === 'rejected' && (
                      <>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E63946]/10 rounded-full">
                          <XCircle size={16} weight="fill" className="text-[#E63946]" />
                          <span className="text-sm font-medium text-[#E63946]">Rejected</span>
                        </div>
                        {po.approval_timeline.stage3_po_approval.rejection_reason && (
                          <p className="text-sm text-[#E63946] bg-[#E63946]/10 p-3 rounded-lg">
                            {po.approval_timeline.stage3_po_approval.rejection_reason}
                          </p>
                        )}
                      </>
                    )}
                    {po.approval_timeline.stage3_po_approval.status === 'pending' && (
                      <>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#8B5CF6]/10 rounded-full">
                          <Clock size={16} weight="fill" className="text-[#8B5CF6]" />
                          <span className="text-sm font-medium text-[#8B5CF6]">Awaiting Approval</span>
                        </div>
                        {po.approval_timeline.stage3_po_approval.pending_with && (
                          <p className="text-sm text-slate-600">
                            Currently with: <span className="font-medium text-[#8B5CF6]">{po.approval_timeline.stage3_po_approval.pending_with}</span>
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Approval data not available</p>
                )}
              </TimelineStage>

              {/* OVERSEAS PO ONLY: Stage 4: PO Issued to Vendor */}
              {po.po_type !== 'local' && (
                <TimelineStage
                  stageNumber={4}
                  title="PO Issued to Vendor"
                  icon={<PaperPlaneTilt size={20} weight="fill" />}
                  isComplete={!!po.approval_timeline.stage4_po_issued?.issued_at}
                  isActive={po.approval_timeline.current_stage === 4}
                  isNotReached={po.approval_timeline.current_stage < 4}
                  data-testid="timeline-stage-4"
                >
                  {po.approval_timeline.stage4_po_issued ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Storefront size={16} className="text-slate-400" />
                        <span className="text-sm font-medium text-slate-800">{po.approval_timeline.stage4_po_issued.vendor_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-slate-400" />
                        <span className="text-sm text-slate-700">{po.approval_timeline.stage4_po_issued.vendor_contact}</span>
                      </div>
                      {po.approval_timeline.stage4_po_issued.issued_at && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <CalendarBlank size={16} className="text-slate-400" />
                          {format(new Date(po.approval_timeline.stage4_po_issued.issued_at), 'MMM d, yyyy')} at {format(new Date(po.approval_timeline.stage4_po_issued.issued_at), 'h:mm a')}
                        </div>
                      )}
                      {po.approval_timeline.stage4_po_issued.notification_sent && (
                        <div className="inline-flex items-center gap-2 px-2 py-1 bg-[#2D6A4F]/10 rounded text-xs text-[#2D6A4F]">
                          <CheckCircle size={14} weight="fill" />
                          Vendor notification sent
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">Not yet issued</p>
                  )}
                </TimelineStage>
              )}

              {/* LOCAL PO ONLY: Stage 4 - Goods Received (final stage for Local POs) */}
              {/* For Local POs, after PO Approval, PM contacts vendor directly - no platform involvement */}
              {po.po_type === 'local' && (
                <TimelineStage
                  stageNumber={4}
                  title="Goods Received"
                  icon={po.status === 'goods_received' || po.status === 'delivered' || po.status === 'closed' 
                    ? <CheckCircle size={20} weight="fill" /> 
                    : <Package size={20} weight="fill" />}
                  isComplete={po.status === 'goods_received' || po.status === 'delivered' || po.status === 'closed'}
                  isActive={po.status === 'approved' || po.status === 'po_issued'}
                  isNotReached={po.approval_timeline.current_stage < 3}
                  data-testid="timeline-stage-local-goods-received"
                >
                  {po.status === 'goods_received' || po.status === 'delivered' || po.status === 'closed' ? (
                    <div className="space-y-2">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2D6A4F]/10 rounded-full">
                        <CheckCircle size={16} weight="fill" className="text-[#2D6A4F]" />
                        <span className="text-sm font-medium text-[#2D6A4F]">Goods Received</span>
                      </div>
                      {po.goods_received_by_name && (
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-slate-400" />
                          <span className="text-sm text-slate-700">Received by: {po.goods_received_by_name}</span>
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">Receiver</span>
                        </div>
                      )}
                      {po.goods_received_at && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <CalendarBlank size={16} className="text-slate-400" />
                          {format(new Date(po.goods_received_at), 'MMM d, yyyy')} at {format(new Date(po.goods_received_at), 'h:mm a')}
                        </div>
                      )}
                      {po.actual_delivery_date && !po.goods_received_at && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <CalendarBlank size={16} className="text-slate-400" />
                          Received: {format(new Date(po.actual_delivery_date), 'MMM d, yyyy')}
                        </div>
                      )}
                      {po.receiving_confirmation?.notes && (
                        <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded mt-2">
                          {po.receiving_confirmation.notes}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#F4A261]/10 rounded-full">
                        <Clock size={16} weight="fill" className="text-[#B45309]" />
                        <span className="text-sm font-medium text-[#B45309]">Awaiting Delivery</span>
                      </div>
                      <p className="text-sm text-slate-500">
                        The Receiver will confirm when goods arrive at the property.
                      </p>
                    </div>
                  )}
                </TimelineStage>
              )}

              {/* OVERSEAS PO ONLY: Stage 5 - Vendor Confirmation */}
              {po.po_type !== 'local' && (
              <TimelineStage
                stageNumber={5}
                title="Vendor Confirmation"
                icon={
                  po.approval_timeline.stage5_vendor_confirmation?.status === 'confirmed' ? <CheckCircle size={20} weight="fill" /> :
                  po.approval_timeline.stage5_vendor_confirmation?.status === 'exception' ? <Warning size={20} weight="fill" /> :
                  <Clock size={20} weight="fill" />
                }
                isComplete={po.approval_timeline.stage5_vendor_confirmation?.status === 'confirmed'}
                isException={po.approval_timeline.stage5_vendor_confirmation?.status === 'exception'}
                isOverdue={po.approval_timeline.stage5_vendor_confirmation?.is_overdue}
                isActive={po.approval_timeline.current_stage === 5}
                isNotReached={po.approval_timeline.current_stage < 5}
                data-testid="timeline-stage-5"
              >
                {po.approval_timeline.stage5_vendor_confirmation ? (
                  <div className="space-y-2">
                    {po.approval_timeline.stage5_vendor_confirmation.status === 'confirmed' && (
                      <>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2D6A4F]/10 rounded-full">
                          <CheckCircle size={16} weight="fill" className="text-[#2D6A4F]" />
                          <span className="text-sm font-medium text-[#2D6A4F]">Confirmed</span>
                        </div>
                      </>
                    )}
                    {po.approval_timeline.stage5_vendor_confirmation.status === 'exception' && (
                      <>
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#F4A261]/10 rounded-full">
                          <Warning size={16} weight="fill" className="text-[#B45309]" />
                          <span className="text-sm font-medium text-[#B45309]">Exception Flagged</span>
                        </div>
                      </>
                    )}
                    {po.approval_timeline.stage5_vendor_confirmation.status === 'pending' && (
                      <>
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${
                          po.approval_timeline.stage5_vendor_confirmation.is_overdue 
                            ? 'bg-[#E63946]/10' 
                            : 'bg-[#F4A261]/10'
                        }`}>
                          {po.approval_timeline.stage5_vendor_confirmation.is_overdue ? (
                            <>
                              <Warning size={16} weight="fill" className="text-[#E63946]" />
                              <span className="text-sm font-medium text-[#E63946]">Overdue - No Response</span>
                            </>
                          ) : (
                            <>
                              <Clock size={16} weight="fill" className="text-[#B45309]" />
                              <span className="text-sm font-medium text-[#B45309]">Awaiting Response</span>
                            </>
                          )}
                        </div>
                      </>
                    )}
                    {po.approval_timeline.stage5_vendor_confirmation.vendor_contact && (
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-slate-400" />
                        <span className="text-sm text-slate-700">{po.approval_timeline.stage5_vendor_confirmation.vendor_contact}</span>
                      </div>
                    )}
                    {po.approval_timeline.stage5_vendor_confirmation.confirmed_at && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <CalendarBlank size={16} className="text-slate-400" />
                        {format(new Date(po.approval_timeline.stage5_vendor_confirmation.confirmed_at), 'MMM d, yyyy')} at {format(new Date(po.approval_timeline.stage5_vendor_confirmation.confirmed_at), 'h:mm a')}
                      </div>
                    )}
                    {po.approval_timeline.stage5_vendor_confirmation.exceptions_noted && (
                      <p className="text-sm text-[#B45309] bg-[#F4A261]/10 p-3 rounded-lg">
                        <span className="font-medium">Exception: </span>
                        {po.approval_timeline.stage5_vendor_confirmation.exceptions_noted}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Awaiting vendor response</p>
                )}
              </TimelineStage>
              )}

              {/* OVERSEAS PO ONLY: Stage 6 - Documents Uploaded */}
              {po.po_type !== 'local' && (
              <TimelineStage
                stageNumber={6}
                title={po.approval_timeline.stage6_documents_uploaded?.title || "Documents Uploaded"}
                icon={<Files size={20} weight="fill" />}
                isComplete={po.approval_timeline.stage6_documents_uploaded?.documents?.length > 0}
                isActive={po.approval_timeline.current_stage === 6}
                isNotReached={po.approval_timeline.current_stage < 6}
                data-testid="timeline-stage-6"
              >
                {po.approval_timeline.stage6_documents_uploaded?.documents?.length > 0 ? (
                  <div className="space-y-3">
                    {/* Invoice Status Banner */}
                    {po.approval_timeline.stage6_documents_uploaded.has_original_invoice && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-100 to-amber-50 rounded-lg border border-amber-200">
                        <CheckCircle size={18} weight="fill" className="text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">Invoice Received</span>
                      </div>
                    )}
                    {po.approval_timeline.stage6_documents_uploaded.has_commercial_invoice_only && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                        <Warning size={18} weight="fill" className="text-amber-500" />
                        <span className="text-sm text-amber-700">Commercial Invoice Only — Original Invoice Not Provided</span>
                      </div>
                    )}
                    
                    {/* Sort documents with invoice types first */}
                    {[...po.approval_timeline.stage6_documents_uploaded.documents]
                      .sort((a, b) => {
                        const priority = {
                          'original_vendor_invoice': 1,
                          'commercial_invoice': 2,
                          'packing_list': 3,
                          'hazmat_documents': 4,
                          'material_data_safety_sheet': 5,
                          'other': 6
                        };
                        return (priority[a.type] || 99) - (priority[b.type] || 99);
                      })
                      .map((doc, idx) => {
                        const isOriginalInvoice = doc.type === 'original_vendor_invoice';
                        const isCommercialInvoice = doc.type === 'commercial_invoice';
                        const hasOriginalInvoice = po.approval_timeline.stage6_documents_uploaded.has_original_invoice;
                        
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              isOriginalInvoice 
                                ? 'bg-gradient-to-r from-amber-100 to-amber-50 border border-amber-300' 
                                : isCommercialInvoice && !hasOriginalInvoice
                                  ? 'bg-amber-50/50 border border-amber-200'
                                  : 'bg-slate-50'
                            }`} 
                            data-testid={`timeline-doc-${idx}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded flex items-center justify-center ${
                                isOriginalInvoice 
                                  ? 'bg-amber-200' 
                                  : isCommercialInvoice && !hasOriginalInvoice
                                    ? 'bg-amber-100'
                                    : 'bg-[#F4A261]/10'
                              }`}>
                                <FileText size={16} weight="duotone" className={
                                  isOriginalInvoice ? 'text-amber-700' : 'text-[#F4A261]'
                                } />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-slate-800">{doc.type_label}</p>
                                  {isOriginalInvoice && (
                                    <span className="px-1.5 py-0.5 bg-amber-500 text-white text-xs font-medium rounded">
                                      PRIMARY
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <span>By: {doc.uploaded_by}</span>
                                  {doc.uploaded_at && (
                                    <>
                                      <span>•</span>
                                      <span>{format(new Date(doc.uploaded_at), 'MMM d, yyyy h:mm a')}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">No documents uploaded yet</p>
                )}
              </TimelineStage>
              )}

              {/* OVERSEAS PO ONLY: Stage 7 - Freight Forwarder Assigned & Shipment Logged */}
              {po.po_type !== 'local' && (
              <TimelineStage
                stageNumber={7}
                title="Freight Forwarder Assigned & Shipment Logged"
                icon={<Truck size={20} weight="fill" />}
                isComplete={po.approval_timeline.stage7_freight_assigned?.is_assigned && po.approval_timeline.stage7_freight_assigned?.pickup_date}
                isActive={po.approval_timeline.current_stage === 7}
                isNotReached={po.approval_timeline.current_stage < 7}
                data-testid="timeline-stage-7"
              >
                {po.approval_timeline.stage7_freight_assigned?.is_assigned ? (
                  <div className="space-y-3">
                    {/* Freight Forwarder Info */}
                    {po.approval_timeline.stage7_freight_assigned.freight_forwarder_name && (
                      <div className="bg-[#0F52BA]/5 p-4 rounded-lg">
                        <p className="text-xs text-[#0F52BA] font-medium mb-2">Freight Forwarder</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Storefront size={16} className="text-[#0F52BA]" />
                            <span className="text-sm font-medium text-slate-800">{po.approval_timeline.stage7_freight_assigned.freight_forwarder_name}</span>
                          </div>
                          {po.approval_timeline.stage7_freight_assigned.contact_person && (
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-slate-400" />
                              <span className="text-sm text-slate-700">{po.approval_timeline.stage7_freight_assigned.contact_person}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Shipment Details */}
                    {po.approval_timeline.stage7_freight_assigned.vessel_or_flight && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-xs text-slate-500">Vessel / Flight</p>
                          <p className="text-sm font-medium text-slate-800">{po.approval_timeline.stage7_freight_assigned.vessel_or_flight}</p>
                          {po.approval_timeline.stage7_freight_assigned.voyage_number && (
                            <p className="text-xs text-slate-500">{po.approval_timeline.stage7_freight_assigned.voyage_number}</p>
                          )}
                        </div>
                        {po.approval_timeline.stage7_freight_assigned.bill_of_lading && (
                          <div className="space-y-1">
                            <p className="text-xs text-slate-500">Bill of Lading</p>
                            <p className="text-sm font-medium text-slate-800">{po.approval_timeline.stage7_freight_assigned.bill_of_lading}</p>
                          </div>
                        )}
                        {po.approval_timeline.stage7_freight_assigned.pickup_date && (
                          <div className="space-y-1">
                            <p className="text-xs text-slate-500">Pickup Date</p>
                            <p className="text-sm font-medium text-slate-800">
                              {format(new Date(po.approval_timeline.stage7_freight_assigned.pickup_date), 'MMM d, yyyy')}
                            </p>
                          </div>
                        )}
                        {po.approval_timeline.stage7_freight_assigned.estimated_arrival && (
                          <div className="space-y-1">
                            <p className="text-xs text-slate-500">Est. Arrival</p>
                            <p className="text-sm font-medium text-[#2D6A4F]">
                              {format(new Date(po.approval_timeline.stage7_freight_assigned.estimated_arrival), 'MMM d, yyyy')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Route */}
                    {(po.approval_timeline.stage7_freight_assigned.port_of_loading || po.approval_timeline.stage7_freight_assigned.port_of_discharge) && (
                      <div className="flex items-center gap-2 text-sm">
                        <Path size={16} className="text-slate-400" />
                        <span className="text-slate-600">
                          {po.approval_timeline.stage7_freight_assigned.port_of_loading || 'N/A'} → {po.approval_timeline.stage7_freight_assigned.port_of_discharge || 'N/A'}
                        </span>
                      </div>
                    )}
                    
                    {!po.approval_timeline.stage7_freight_assigned.vessel_or_flight && (
                      <p className="text-sm text-[#F4A261]">Freight forwarder assigned - awaiting shipment details</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-500 italic" data-testid="ff-not-assigned-msg">
                      Freight forwarder not yet assigned
                    </p>
                    {isPurchasingManager && (
                      <Button
                        size="sm"
                        onClick={openAssignFFDialog}
                        className="bg-[#0F52BA] hover:bg-[#0A3A8C] text-white"
                        data-testid="inline-assign-ff-btn"
                      >
                        <Truck size={16} className="mr-2" />
                        Assign Freight Forwarder
                      </Button>
                    )}
                  </div>
                )}
              </TimelineStage>
              )}

              {/* OVERSEAS PO ONLY: Stage 8 - In Transit */}
              {po.po_type !== 'local' && (
              <TimelineStage
                stageNumber={8}
                title="In Transit"
                icon={
                  po.approval_timeline.stage8_in_transit?.status === 'arrived' ? <CheckCircle size={20} weight="fill" /> :
                  po.approval_timeline.stage8_in_transit?.status === 'in_transit' ? <Truck size={20} weight="fill" /> :
                  <Clock size={20} weight="fill" />
                }
                isComplete={po.approval_timeline.stage8_in_transit?.status === 'arrived'}
                isActive={po.approval_timeline.current_stage === 8 && po.approval_timeline.stage8_in_transit?.status === 'in_transit'}
                isNotReached={po.approval_timeline.current_stage < 8}
                data-testid="timeline-stage-8"
              >
                {po.approval_timeline.stage8_in_transit?.status !== 'not_started' ? (
                  <div className="space-y-3">
                    {/* Status Badge */}
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${
                      po.approval_timeline.stage8_in_transit?.status === 'arrived' 
                        ? 'bg-[#2D6A4F]/10' 
                        : 'bg-[#0F52BA]/10'
                    }`}>
                      {po.approval_timeline.stage8_in_transit?.status === 'arrived' ? (
                        <>
                          <CheckCircle size={16} weight="fill" className="text-[#2D6A4F]" />
                          <span className="text-sm font-medium text-[#2D6A4F]">Arrived</span>
                        </>
                      ) : (
                        <>
                          <Truck size={16} weight="fill" className="text-[#0F52BA]" />
                          <span className="text-sm font-medium text-[#0F52BA]">In Transit</span>
                        </>
                      )}
                    </div>
                    
                    {/* Current ETA */}
                    {po.approval_timeline.stage8_in_transit?.current_eta && (
                      <div className="flex items-center gap-2">
                        <Package size={16} className="text-[#2D6A4F]" />
                        <span className="text-sm text-slate-600">Current ETA:</span>
                        <span className="text-sm font-bold text-[#2D6A4F]">
                          {format(new Date(po.approval_timeline.stage8_in_transit.current_eta), 'MMM d, yyyy')}
                        </span>
                      </div>
                    )}
                    
                    {/* Last ETA Update */}
                    {po.approval_timeline.stage8_in_transit?.last_eta_update && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <CalendarBlank size={16} className="text-slate-400" />
                        Last updated: {format(new Date(po.approval_timeline.stage8_in_transit.last_eta_update), 'MMM d, yyyy h:mm a')}
                      </div>
                    )}
                    
                    {/* ETA Changes History */}
                    {po.approval_timeline.stage8_in_transit?.eta_changes?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-600 mb-3">ETA Change History</p>
                        <div className="space-y-2">
                          {po.approval_timeline.stage8_in_transit.eta_changes.map((change, idx) => (
                            <div key={idx} className="bg-slate-50 p-3 rounded-lg" data-testid={`eta-change-${idx}`}>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-slate-500 line-through">
                                  {format(new Date(change.original_date), 'MMM d')}
                                </span>
                                <span className="text-slate-400">→</span>
                                <span className="font-medium text-slate-800">
                                  {format(new Date(change.revised_date), 'MMM d, yyyy')}
                                </span>
                              </div>
                              {change.reason && (
                                <p className="text-xs text-slate-500 mt-1">Reason: {change.reason}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                Changed: {format(new Date(change.changed_at), 'MMM d, h:mm a')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Shipment not yet in transit</p>
                )}
              </TimelineStage>
              )}

              {/* OVERSEAS PO ONLY: Stage 9 - Goods Received (final stage for Overseas POs) */}
              {/* Confirmed by Receiver role only - separation of duties principle */}
              {po.po_type !== 'local' && (
                <TimelineStage
                  stageNumber={9}
                  title="Goods Received"
                  icon={po.status === 'goods_received' || po.status === 'delivered' || po.status === 'closed' 
                    ? <CheckCircle size={20} weight="fill" /> 
                    : <Package size={20} weight="fill" />}
                  isComplete={po.status === 'goods_received' || po.status === 'delivered' || po.status === 'closed'}
                  isActive={po.approval_timeline.stage8_in_transit?.status === 'arrived' && po.status !== 'goods_received' && po.status !== 'delivered' && po.status !== 'closed'}
                  isNotReached={(po.approval_timeline.current_stage < 8 || po.approval_timeline.stage8_in_transit?.status !== 'arrived') && po.status !== 'goods_received' && po.status !== 'delivered' && po.status !== 'closed'}
                  data-testid="timeline-stage-9-goods-received"
                >
                  {po.status === 'goods_received' || po.status === 'delivered' || po.status === 'closed' ? (
                    <div className="space-y-2">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2D6A4F]/10 rounded-full">
                        <CheckCircle size={16} weight="fill" className="text-[#2D6A4F]" />
                        <span className="text-sm font-medium text-[#2D6A4F]">Goods Received & PO Closed</span>
                      </div>
                      {po.goods_received_by_name && (
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-slate-400" />
                          <span className="text-sm text-slate-700">Received by: {po.goods_received_by_name}</span>
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">Receiver</span>
                        </div>
                      )}
                      {po.goods_received_at && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <CalendarBlank size={16} className="text-slate-400" />
                          {format(new Date(po.goods_received_at), 'MMM d, yyyy')} at {format(new Date(po.goods_received_at), 'h:mm a')}
                        </div>
                      )}
                      {po.actual_delivery_date && !po.goods_received_at && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <CalendarBlank size={16} className="text-slate-400" />
                          Received: {format(new Date(po.actual_delivery_date), 'MMM d, yyyy')}
                        </div>
                      )}
                      {po.receiving_confirmation?.notes && (
                        <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded mt-2">
                          {po.receiving_confirmation.notes}
                        </p>
                      )}
                      {/* Inventory update note */}
                      <div className="mt-3 p-3 bg-[#2D6A4F]/5 rounded-lg border border-[#2D6A4F]/10">
                        <div className="flex items-start gap-2">
                          <Package size={16} className="text-[#2D6A4F] mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-[#2D6A4F]">Inventory Updated</p>
                            <p className="text-xs text-slate-600 mt-1">
                              Stock levels have been updated in the Item Master List. Non-inventory items have been expensed.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#F4A261]/10 rounded-full">
                        <Clock size={16} weight="fill" className="text-[#B45309]" />
                        <span className="text-sm font-medium text-[#B45309]">Awaiting Goods Receipt</span>
                      </div>
                      <p className="text-sm text-slate-500">
                        The Receiver will confirm when goods arrive and have been inspected at the property.
                      </p>
                    </div>
                  )}
                </TimelineStage>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Timeline data not available</p>
            </div>
          )}
        </div>
      </div>

      {/* Delivery and Shipment Summary - Only shown for Closed POs */}
      {po.status === 'closed' && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid="delivery-summary-section">
          <div className="p-4 border-b border-slate-100 bg-[#2D6A4F]/5">
            <h2 className="text-lg font-semibold text-[#2D6A4F]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <SealCheck size={24} weight="duotone" className="inline-block mr-2" />
              Delivery and Shipment Summary
            </h2>
            <p className="text-sm text-slate-500 mt-1">Freight forwarder and shipment information</p>
          </div>
          <div className="p-6">
            {po.delivery_summary ? (
              <div className="space-y-6">
                {/* Freight Forwarder Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-[#0F52BA]/5 p-4 rounded-lg">
                    <p className="text-xs text-[#0F52BA] font-semibold mb-3 uppercase tracking-wider">Freight Forwarder</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Storefront size={18} className="text-[#0F52BA]" />
                        <span className="font-medium text-slate-800">{po.delivery_summary.freight_forwarder_name || 'Not assigned'}</span>
                      </div>
                      {po.delivery_summary.freight_forwarder_contact && (
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-slate-400" />
                          <span className="text-sm text-slate-600">{po.delivery_summary.freight_forwarder_contact}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Route Info */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-xs text-slate-600 font-semibold mb-3 uppercase tracking-wider">Shipping Route</p>
                    <div className="flex items-center gap-3">
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-800">{po.delivery_summary.port_of_loading || 'N/A'}</p>
                        <p className="text-xs text-slate-500">Port of Loading</p>
                      </div>
                      <Path size={24} className="text-slate-400" />
                      <div className="text-center">
                        <p className="text-sm font-medium text-slate-800">{po.delivery_summary.port_of_discharge || 'N/A'}</p>
                        <p className="text-xs text-slate-500">Port of Discharge</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipment Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Vessel / Flight</p>
                    <p className="text-sm font-medium text-slate-800">{po.delivery_summary.vessel_or_flight || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Voyage / Flight #</p>
                    <p className="text-sm font-medium text-slate-800">{po.delivery_summary.voyage_number || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Bill of Lading / AWB</p>
                    <p className="text-sm font-medium text-slate-800">{po.delivery_summary.bill_of_lading || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500">Original ETA</p>
                    <p className="text-sm font-medium text-slate-800">
                      {po.delivery_summary.original_eta ? format(new Date(po.delivery_summary.original_eta), 'MMM d, yyyy') : '-'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Package size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Delivery summary not available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              Upload Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 font-medium">Invoice Uploads</p>
              <p className="text-xs text-blue-700 mt-1">
                Any invoice / sales document is acceptable — commercial invoice, proforma invoice, sales order, online retailer invoice (Amazon, supplier website), supplier receipt — as long as it shows item descriptions and values.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger data-testid="upload-doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="original_vendor_invoice" className="font-medium">Invoice</SelectItem>
                  <SelectItem value="packing_list">Packing List</SelectItem>
                  <SelectItem value="hazmat_documents">Hazmat Documents (if applicable)</SelectItem>
                  <SelectItem value="material_data_safety_sheet">Material Data Safety Sheet (if applicable)</SelectItem>
                  <SelectItem value="certificate_of_origin">Certificate of Origin</SelectItem>
                  <SelectItem value="bill_of_lading">Bill of Lading</SelectItem>
                  <SelectItem value="other">Other Supporting Documents</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Document URL</Label>
              <Input
                value={documentUrl}
                onChange={(e) => setDocumentUrl(e.target.value)}
                placeholder="https://example.com/document.pdf"
                data-testid="upload-doc-url-input"
              />
              <p className="text-xs text-slate-500">Enter the URL where the document is hosted</p>
            </div>

            <div className="space-y-2">
              <Label>Filename</Label>
              <Input
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder={documentType === 'original_vendor_invoice' ? 'invoice_001.pdf' : 'document_001.pdf'}
                data-testid="upload-doc-filename-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="secondary" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleUploadDocument}
                disabled={submitting || !documentUrl || !filename}
                data-testid="submit-upload-btn"
              >
                {submitting ? <Spinner size={20} className="animate-spin mr-2" /> : null}
                Upload Document
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receiving Confirmation Dialog */}
      <Dialog open={receivingDialogOpen} onOpenChange={setReceivingDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={24} className="text-[#059669]" />
              Confirm Goods Received
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Enter the quantity received for each line item. If any quantity differs from the ordered amount, 
              a variance note will be required.
            </p>
            
            {/* Line Items Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Item</th>
                    <th className="px-4 py-3 text-center font-semibold text-slate-700 w-24">Ordered</th>
                    <th className="px-4 py-3 text-center font-semibold text-slate-700 w-32">Received</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Variance Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {receivingItems.map((item, idx) => (
                    <tr key={idx} className={item.has_variance ? 'bg-red-50' : ''}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800">{item.item_name}</span>
                        <br />
                        <span className="text-xs text-slate-500">{item.unit}</span>
                      </td>
                      <td className="px-4 py-3 text-center font-medium text-slate-700">
                        {item.ordered_quantity}
                      </td>
                      <td className="px-4 py-3">
                        <Input
                          type="number"
                          min="0"
                          value={item.received_quantity}
                          onChange={(e) => updateReceivedQuantity(idx, e.target.value)}
                          className={`w-24 text-center ${item.has_variance ? 'border-red-300 bg-red-50' : ''}`}
                          data-testid={`received-qty-${idx}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {item.has_variance ? (
                          <Input
                            value={item.variance_note}
                            onChange={(e) => updateVarianceNote(idx, e.target.value)}
                            placeholder="Explain variance (required)"
                            className="text-sm border-amber-300"
                            data-testid={`variance-note-${idx}`}
                          />
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Variance Warning */}
            {receivingItems.some(item => item.has_variance) && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Variances Detected</p>
                  <p>You must provide a variance note for each item where the received quantity differs from the ordered quantity.</p>
                </div>
              </div>
            )}
            
            {/* Overall Notes */}
            <div className="space-y-2">
              <Label>Overall Receiving Notes (Optional)</Label>
              <textarea
                value={receivingNotes}
                onChange={(e) => setReceivingNotes(e.target.value)}
                placeholder="Any additional notes about the receiving..."
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm resize-none"
                rows={3}
                data-testid="receiving-overall-notes"
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setReceivingDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmReceiving}
                disabled={receivingSubmitting}
                className="bg-[#059669] hover:bg-[#047857]"
                data-testid="submit-receiving-btn"
              >
                {receivingSubmitting ? (
                  <Spinner size={18} className="animate-spin mr-2" />
                ) : (
                  <CheckCircle size={18} className="mr-2" />
                )}
                Confirm Goods Received
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Freight Forwarder Dialog (inline from Stage 7) */}
      <Dialog open={assignFFDialogOpen} onOpenChange={setAssignFFDialogOpen}>
        <DialogContent className="max-w-md" data-testid="assign-ff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck size={20} className="text-[#0F52BA]" />
              Assign Freight Forwarder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {po && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm font-semibold text-slate-800">{po.po_number}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {po.vendor_name} • {po.country_of_origin || po.vendor_country || 'Overseas'}
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="ff-select" className="text-sm">Select Freight Forwarder</Label>
              <Select value={selectedFFId} onValueChange={setSelectedFFId}>
                <SelectTrigger id="ff-select" data-testid="ff-select-trigger">
                  <SelectValue placeholder="Choose a freight forwarder" />
                </SelectTrigger>
                <SelectContent>
                  {freightForwarders.map(ff => (
                    <SelectItem key={ff.id} value={ff.id} data-testid={`ff-option-${ff.id}`}>
                      {ff.company_name}
                      {ff.regions_served && ff.regions_served.length > 0 && (
                        <span className="text-xs text-slate-400 ml-2">
                          ({ff.regions_served.join(', ')})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {freightForwarders.length === 0 && (
                <p className="text-xs text-[#F4A261] mt-1">
                  No freight forwarders available. Add one under Freight Forwarders first.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="cargo-ready-date" className="text-sm">Estimated Cargo Ready Date</Label>
              <Input
                id="cargo-ready-date"
                type="date"
                value={cargoReadyDate}
                onChange={(e) => setCargoReadyDate(e.target.value)}
                data-testid="cargo-ready-date-input"
              />
            </div>

            <div>
              <Label htmlFor="ff-notes" className="text-sm">Notes (optional)</Label>
              <Input
                id="ff-notes"
                placeholder="Special handling instructions, pickup details..."
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                data-testid="ff-notes-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setAssignFFDialogOpen(false)}
                disabled={assigningFF}
                data-testid="ff-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignFF}
                disabled={assigningFF || !selectedFFId || !cargoReadyDate}
                className="bg-[#0F52BA] hover:bg-[#0A3A8C] text-white"
                data-testid="ff-confirm-btn"
              >
                {assigningFF ? (
                  <Spinner size={16} className="mr-2 animate-spin" />
                ) : (
                  <CheckCircle size={16} className="mr-2" />
                )}
                Assign & Notify
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrderDetailPage;
